import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useState } from 'react'
import { routes } from '@/routes'
import { useLocales, type CountryCode } from '@sevenrooms/locales'
import { Route, Switch, Redirect } from '@sevenrooms/navigation'
import { useRequestDetailQuery } from '../domain/request'
import { useReservationsRoute, useVenue, useWidgetLanguage } from '../hooks'
import { reservationWidgetMessages } from '../reservationWidgetMessages'
import { useGetVenueTagsQuery, useModals, useReservationFormState } from '../store'
import { CheckoutPage } from './Checkout'
import { SearchAndAvailability } from './Search'
import { UpgradesPage } from './Upgrades'

const createPath = routes.reservations.create

export function Create() {
  const { formatMessage } = useLocales()
  const { id: venueId } = useVenue()
  const { showErrorModal } = useModals()
  const { requestId, clearQueryParams, hasPaymentError } = useReservationsRoute()
  const { selectedLanguage } = useWidgetLanguage()
  const { data, isLoading, isError } = useRequestDetailQuery(requestId ? { requestId } : skipToken)
  const { updateFormState } = useReservationFormState()
  const [shouldDisplayLoader, setShouldDisplayLoader] = useState(true)
  // calling get query tags earlier to preload them
  useGetVenueTagsQuery({ venueId, lang: selectedLanguage })

  useEffect(() => {
    if (isError) {
      showErrorModal()
    }
  }, [isError, showErrorModal])

  useEffect(() => {
    if (hasPaymentError) {
      showErrorModal(
        formatMessage(reservationWidgetMessages.resPaymentFailedModalBody),
        formatMessage(reservationWidgetMessages.resPaymentFailedModalHeader),
        true
      )
    }
    // formatMessage dependency triggers use effect to be executed on each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPaymentError, showErrorModal])

  useEffect(() => {
    if (!isLoading && data) {
      setShouldDisplayLoader(false)

      let errorMessage
      if (data.venueId !== venueId) {
        errorMessage = formatMessage(reservationWidgetMessages.resWidgetRequestErrorMessageInvalid)
      } else if (!data.isActive) {
        errorMessage = formatMessage(reservationWidgetMessages.resWidgetRequestErrorMessageInactive)
      }

      if (errorMessage) {
        showErrorModal(errorMessage)
        clearQueryParams('request_id')
        return
      }

      updateFormState({
        startDate: data.date,
        partySize: data.maxGuests,
        emailAddress: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
        phoneCountryCode: data.phoneNumberLocale?.toLowerCase() as CountryCode,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, data, isLoading, showErrorModal, updateFormState, formatMessage])

  return (
    <Switch>
      <Route path={createPath.search.path}>
        <SearchAndAvailability shouldDisplayLoader={!!(requestId && shouldDisplayLoader)} />
      </Route>
      <Route path={createPath.upgrades.path}>
        <UpgradesPage />
      </Route>
      <Route path={createPath.checkout.path}>
        <CheckoutPage />
      </Route>
      <Redirect to={createPath.search.path} />
    </Switch>
  )
}
